/**
 * ExplainerVideoHelpModal
 *
 * Simple help modal explaining how to add a YouTube explainer video to the
 * survey intro. Click-outside and Escape close behaviors.
 */
import React, { useEffect, useRef } from 'react';
import { X, Youtube, Info } from 'lucide-react';

export function ExplainerVideoHelpModal({ isOpen, onClose }) {
  if (!isOpen) return null;
  
  const modalRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Close modal when pressing Escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div 
        ref={modalRef}
        className="relative w-full max-w-lg rounded-lg bg-white p-5 shadow-lg dark:bg-gray-800 animate-in fade-in zoom-in duration-200"
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="flex items-center gap-2 mb-3">
          <Youtube className="h-5 w-5 text-red-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            YouTube Explainer Video
          </h3>
        </div>
        
        <div className="text-sm text-gray-600 dark:text-gray-300 space-y-3">
          <p>
            Add a YouTube video that will be shown to participants before they answer your survey questions.
          </p>
          
          <div className="flex items-start gap-2 bg-primary-50 dark:bg-primary-900/20 p-3 rounded-md">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-primary-600 dark:text-primary-400" />
            <div>
              <p className="text-sm text-primary-800 dark:text-primary-300">
                Simply paste a YouTube video URL (public or unlisted). The video will be embedded at the top of your survey. <strong>Note:</strong> At least one survey question is still required.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
} 